import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Capacitor } from '@capacitor/core'
import { ScreenOrientation } from '@capacitor/screen-orientation'
import { isNativeApp } from '../lib/native'

interface Props {
  title: string
  onClose: () => void
  /** The content scrolls (a table) rather than being drawn to the stage's size (a diagram) */
  scroll?: boolean
  /** Controls shown in the header, before the zoom reset and the close button */
  actions?: ReactNode
  /** The diagram, drawn to fill the given size (CSS px) */
  children: (size: { width: number; height: number }) => ReactNode
}

const MAX_SCALE = 5

interface View {
  scale: number
  x: number
  y: number
}

/**
 * One diagram over the whole screen. Two fingers zoom and pan it (the diagram itself keeps
 * its one-finger behaviour, the anchor); × or Esc closes it.
 */
export default function Fullscreen({ title, onClose, scroll = false, actions, children }: Props) {
  const stage = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 })
  // The live view for the gesture maths, so a move never waits on a render.
  const viewRef = useRef(view)
  viewRef.current = view

  useEffect(() => {
    const el = stage.current
    if (!el) return
    let w = 0
    let h = 0
    const measure = () => {
      const nw = Math.floor(el.clientWidth)
      const nh = Math.floor(el.clientHeight)
      if (nw === w && nh === h) return
      w = nw
      h = nh
      setSize({ width: w, height: h })
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // A diagram is a landscape thing: the app turns sideways while one is full screen (the
  // website cannot force that; a browser's own lock is tried and failures ignored).
  useEffect(() => {
    if (isNativeApp && Capacitor.isPluginAvailable('ScreenOrientation')) {
      void ScreenOrientation.lock({ orientation: 'landscape' }).catch(() => undefined)
      return () => {
        void ScreenOrientation.unlock().catch(() => undefined)
      }
    }
    const so = screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> }
    void so.lock?.('landscape').catch(() => undefined)
    return () => {
      try {
        screen.orientation.unlock()
      } catch {
        /* not locked */
      }
    }
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    // The page behind must not scroll, and the browser must not zoom the page itself: the
    // two-finger gesture belongs to the stage. WebKit only honours the latter through its
    // gesture events and the viewport, so both are switched for as long as the overlay is up.
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const meta = document.querySelector<HTMLMetaElement>('meta[name=viewport]')
    const prevViewport = meta?.content ?? null
    if (meta) meta.content = `${prevViewport}, maximum-scale=1, user-scalable=no`
    const block = (e: Event) => e.preventDefault()
    document.addEventListener('gesturestart', block)
    document.addEventListener('gesturechange', block)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('gesturestart', block)
      document.removeEventListener('gesturechange', block)
      document.body.style.overflow = prevOverflow
      if (meta && prevViewport != null) meta.content = prevViewport
    }
  }, [onClose])

  // Pinch: scale about the fingers' midpoint, pan with their movement. Everything the gesture
  // needs lives in refs, so it works the moment the second finger lands.
  const gesture = useRef<{ dist: number; mid: { x: number; y: number }; view: View } | null>(null)
  useEffect(() => {
    const el = stage.current
    if (!el) return
    const points = (ev: TouchEvent) => {
      const r = el.getBoundingClientRect()
      const a = ev.touches[0]
      const b = ev.touches[1]
      return {
        dist: Math.max(1, Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)),
        mid: { x: (a.clientX + b.clientX) / 2 - r.left, y: (a.clientY + b.clientY) / 2 - r.top },
      }
    }
    const start = (ev: TouchEvent) => {
      if (ev.touches.length !== 2) {
        gesture.current = null
        return
      }
      ev.preventDefault()
      gesture.current = { ...points(ev), view: viewRef.current }
    }
    const move = (ev: TouchEvent) => {
      const g = gesture.current
      if (!g || ev.touches.length !== 2) return
      ev.preventDefault()
      const p = points(ev)
      const scale = Math.max(1, Math.min(MAX_SCALE, (g.view.scale * p.dist) / g.dist))
      // Keep the point that was under the fingers' midpoint under it still.
      const k = scale / g.view.scale
      const x = p.mid.x - k * (g.mid.x - g.view.x)
      const y = p.mid.y - k * (g.mid.y - g.view.y)
      setView(clamp({ scale, x, y }, el.clientWidth, el.clientHeight))
    }
    const end = (ev: TouchEvent) => {
      if (ev.touches.length < 2) gesture.current = null
    }
    el.addEventListener('touchstart', start, { passive: false })
    el.addEventListener('touchmove', move, { passive: false })
    el.addEventListener('touchend', end, { passive: true })
    el.addEventListener('touchcancel', end, { passive: true })
    return () => {
      el.removeEventListener('touchstart', start)
      el.removeEventListener('touchmove', move)
      el.removeEventListener('touchend', end)
      el.removeEventListener('touchcancel', end)
    }
  }, [])

  return createPortal(
    <div className="fullscreen" role="dialog" aria-label={title}>
      <header className="fullscreen-head">
        <span className="fullscreen-title">{title}</span>
        {actions}
        {view.scale > 1 && (
          <button className="link fullscreen-reset" onClick={() => setView({ scale: 1, x: 0, y: 0 })}>
            {Math.round(view.scale * 100)}% · reset
          </button>
        )}
        <button className="link fullscreen-close" onClick={onClose} aria-label="Close full screen">
          ×
        </button>
      </header>
      <div ref={stage} className={`fullscreen-stage${scroll ? ' scroll' : ''}`}>
        {size && (
          <div
            className="fullscreen-zoom"
            style={{
              width: size.width,
              minHeight: size.height,
              height: scroll ? undefined : size.height,
              transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            }}
          >
            {children(size)}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

/** Keep the zoomed diagram covering the stage: no empty gaps at the edges. */
function clamp(v: View, w: number, h: number): View {
  const minX = w - w * v.scale
  const minY = h - h * v.scale
  return { scale: v.scale, x: Math.min(0, Math.max(minX, v.x)), y: Math.min(0, Math.max(minY, v.y)) }
}
